import { createGlobalStyle } from 'styled-components';
import { theme } from './theme';

export const GlobalStyles = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    font-size: 14px;
    scroll-behavior: smooth;
  }

  body {
    height: 100%;
    font-family: ${theme.typography.fontFamily};
    font-weight: ${theme.typography.fontWeight.regular};
    font-size: ${theme.typography.fontSize.base};
    line-height: ${theme.typography.lineHeight.normal};
    color: ${theme.colors.text};
    background: ${theme.colors.background};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    overflow-x: hidden;
  }

  #root {
    height: 100%;
    min-height: 100vh;
    width: 100%;
  }

  /* Portal-specific improvements */
  body {
    overflow-x: hidden;
  }

  /* Typography */
  h1, h2, h3, h4, h5, h6 {
    font-weight: ${theme.typography.fontWeight.semiBold};
    color: ${theme.colors.text};
    line-height: ${theme.typography.lineHeight.tight};
    margin-bottom: ${theme.spacing.md};
  }

  h1 {
    font-size: ${theme.typography.fontSize['3xl']};
    font-weight: ${theme.typography.fontWeight.bold};
  }

  h2 {
    font-size: ${theme.typography.fontSize['2xl']};
  }

  h3 {
    font-size: ${theme.typography.fontSize.xl};
  }

  p {
    margin-bottom: ${theme.spacing.md};
    line-height: ${theme.typography.lineHeight.relaxed};
  }

  /* Buttons */
  button {
    font-family: ${theme.typography.fontFamily};
    cursor: pointer;
    border: none;
    outline: none;
    transition: ${theme.transitions.default};
  }

  button:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }

  /* Form Elements */
  input, textarea, select {
    font-family: ${theme.typography.fontFamily};
    outline: none;
    transition: ${theme.transitions.default};
  }

  input:focus-visible,
  textarea:focus-visible,
  select:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }

  /* Links */
  a {
    text-decoration: none;
    color: ${theme.colors.primary};
    transition: ${theme.transitions.default};
  }

  a:hover {
    color: ${theme.colors.primaryLight};
    text-decoration: underline;
  }

  /* Lists */
  ul, ol {
    list-style: none;
  }

  /* Images */
  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  /* Tables */
  table {
    width: 100%;
    border-collapse: collapse;
  }

  /* Selection */
  ::selection {
    background-color: ${theme.colors.primary}30;
    color: ${theme.colors.text};
  }

  /* Scrollbar Styling - Enhanced */
  ::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  ::-webkit-scrollbar-track {
    background: ${theme.colors.secondary};
    border-radius: ${theme.borderRadius.full};
  }

  ::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accentTeal} 100%);
    border-radius: ${theme.borderRadius.full};
    border: 2px solid ${theme.colors.secondary};
    transition: background 0.3s ease;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, ${theme.colors.primaryLight} 0%, ${theme.colors.accentTeal} 100%);
  }

  /* Firefox scrollbar */
  * {
    scrollbar-width: thin;
    scrollbar-color: ${theme.colors.primary} ${theme.colors.secondary};
  }

  /* Loading Animation */
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideIn {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  }

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }

  /* Print Styles */
  @media print {
    * {
      background: white !important;
      color: black !important;
      box-shadow: none !important;
    }
    
    .no-print {
      display: none !important;
    }
  }

  /* Reduced Motion */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }

  /* Focus Visible for Accessibility */
  :focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
    border-radius: ${theme.borderRadius.sm};
  }

  /* Ensure all clickable elements have proper cursor */
  [role="button"],
  button,
  a,
  select,
  input[type="checkbox"],
  input[type="radio"],
  input[type="file"],
  label {
    cursor: pointer;
  }

  /* Fix pointer events for clickable elements */
  button:not(:disabled),
  a:not([aria-disabled="true"]),
  [role="button"]:not([aria-disabled="true"]),
  select:not(:disabled),
  input:not(:disabled),
  label {
    pointer-events: auto !important;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
    touch-action: manipulation;
  }

  /* Disabled elements */
  button:disabled,
  [disabled],
  [aria-disabled="true"],
  select:disabled,
  input:disabled {
    cursor: not-allowed !important;
    pointer-events: none;
    opacity: 0.6;
  }

  /* Prevent text selection on interactive elements */
  button,
  [role="button"],
  a {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }

  /* Ensure clickable styled components have proper interaction */
  .clickable,
  [data-clickable="true"] {
    cursor: pointer !important;
    pointer-events: auto !important;
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }

  /* Ensure modal content is clickable */
  [role="dialog"],
  [role="dialog"] > * {
    pointer-events: auto;
  }

  /* Prevent decorative pseudo-elements from blocking clicks */
  button::before,
  button::after,
  a::before,
  a::after,
  div::before,
  div::after,
  span::before,
  span::after {
    pointer-events: none;
  }

  /* Ensure parent containers with positioning don't interfere */
  .modal-backdrop,
  .overlay {
    pointer-events: auto;
  }

  /* Ensure form elements and labels work correctly */
  label {
    cursor: pointer;
    pointer-events: auto;
  }

  label input,
  label select,
  label textarea {
    pointer-events: auto;
  }
`;

